/*eslint max-lines-per-function: ["error", 180]*/
import React, { useCallback, useEffect } from 'react';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { VerticalBarChart } from '../../../common/components/Charts/VerticalBarChart';
import {
  LabelWith4CountsValue,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';

import {
  DateRangeFilterApplied,
  SuperAdminDateRangeFilter,
} from './SuperAdminDateRangeFilter';
import {
  DateRangeState,
  initDefaultLastDaysRange,
  toDateEnd,
  toDateStart,
} from './SuperAdminTopCompaniesBase';

export const SuperAdminExamsByTypeTab: React.FC = () => {
  const spd = useSpdCore();
  const [dataOnlineExams, setDataOnlineExams] = React.useState<
    LabelWith4CountsValue[]
  >([]);
  const [dataOnlineExamsByDays, setDataOnlineExamsByDays] = React.useState<
    LabelWith4CountsValue[]
  >([]);
  const [filters, setFilters] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [applied, setApplied] = React.useState<DateRangeFilterApplied>({
    dateFrom: '',
    dateTo: '',
  });
  const [errors, setErrors] = React.useState<string[]>([]);
  const [errorsMonthly, setErrorsMonthly] = React.useState<string[]>([]);
  const [filtersMonthly, setFiltersMonthly] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });

  const loadByDays = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrors([]);
      spd.reports
        .reportByTypeExams(
          true,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
          a.companyId,
        )
        .then(data => setDataOnlineExamsByDays(data))
        .catch(async error => {
          setDataOnlineExamsByDays([]);
          console.error('Error during report by type exams', error);
          setErrors(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  const loadByMonths = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrorsMonthly([]);
      spd.reports
        .reportByTypeExams(
          undefined,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
          a.companyId,
        )
        .then(data => setDataOnlineExams(data))
        .catch(async error => {
          setDataOnlineExams([]);
          console.error('Error during report by type exams (monthly)', error);
          setErrorsMonthly(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  useEffect(() => {
    void initDefaultLastDaysRange(
      spd.params.getReportLastDays,
      range => {
        setFilters(range);
        setApplied(range);
        loadByDays(range);
      },
      async error => {
        console.error('Error during report last days param fetch', error);
        setErrors(await responseErrors(error as ResponseError));
      },
    );
  }, [spd.params, loadByDays]);

  useEffect(() => {
    loadByMonths({ dateFrom: '', dateTo: '' });
  }, [loadByMonths]);

  return (
    <>
      <SuperAdminDateRangeFilter
        filters={filters}
        setFilters={setFilters}
        withCompany={true}
        onApply={a => {
          setErrors([]);
          setDataOnlineExamsByDays([]);
          setApplied(a);
          loadByDays(a);
        }}
      />
      <ValidationAlert
        show={errors.length > 0}
        errors={errors}
        className="mt-3"
      />
      {errors.length === 0 && (
        <VerticalBarChart
          title={`Zestawienie zakończonych badań wg rodzaju badania w zakresie: ${applied.dateFrom} - ${applied.dateTo}`}
          labels={dataOnlineExamsByDays.map(o => o.label)}
          data={dataOnlineExamsByDays.map(o => o.count1)}
          data2={dataOnlineExamsByDays.map(o => o.count2)}
          data3={dataOnlineExamsByDays.map(o => o.count3)}
          data4={dataOnlineExamsByDays.map(o => o.count4)}
          datasetLabel="Badania standardowe"
          backgroundColor="rgba(220, 38, 38, 0.5)"
          borderColor="rgba(220, 38, 38, 1)"
          datasetLabel2="Badania online"
          backgroundColor2="rgba(245, 158, 11, 0.5)"
          borderColor2="rgba(245, 158, 11, 1)"
          datasetLabel3="Portal badanego"
          backgroundColor3="rgba(16, 185, 129, 0.5)"
          borderColor3="rgba(16, 185, 129, 1)"
          datasetLabel4="Badania ze skierowań zewnętrznych"
          backgroundColor4="rgba(88, 28, 135, 0.5)"
          borderColor4="rgba(88, 28, 135, 1)"
          key="examsByTypeByDays"
        />
      )}
      <SuperAdminDateRangeFilter
        filters={filtersMonthly}
        setFilters={setFiltersMonthly}
        withCompany={true}
        monthPicker={true}
        applyLabel="Pokaż zestawienie miesięczne"
        onApply={a => {
          setErrorsMonthly([]);
          setDataOnlineExams([]);
          loadByMonths(a);
        }}
      />
      <ValidationAlert
        show={errorsMonthly.length > 0}
        errors={errorsMonthly}
        className="mt-3"
      />
      {errorsMonthly.length === 0 && (
        <VerticalBarChart
          title="Zestawienie zakończonych badań w podziale na miesiące wg rodzaju badania"
          labels={dataOnlineExams.map(o => o.label)}
          data={dataOnlineExams.map(o => o.count1)}
          data2={dataOnlineExams.map(o => o.count2)}
          data3={dataOnlineExams.map(o => o.count3)}
          data4={dataOnlineExams.map(o => o.count4)}
          datasetLabel="Badania standardowe"
          backgroundColor="rgba(220, 38, 38, 0.5)"
          borderColor="rgba(220, 38, 38, 1)"
          datasetLabel2="Badania online"
          backgroundColor2="rgba(245, 158, 11, 0.5)"
          borderColor2="rgba(245, 158, 11, 1)"
          datasetLabel3="Portal badanego"
          backgroundColor3="rgba(16, 185, 129, 0.5)"
          borderColor3="rgba(16, 185, 129, 1)"
          datasetLabel4="Badania ze skierowań zewnętrznych"
          backgroundColor4="rgba(88, 28, 135, 0.5)"
          borderColor4="rgba(88, 28, 135, 1)"
          key="examsByTypeByMonths"
        />
      )}
    </>
  );
};
